import React, {CSSProperties, useEffect, useState} from "react";
import {observer} from "mobx-react-lite";
import {ErrorBoundary} from "react-error-boundary";
import ErrorComponent from "../ErrorComponent/ErrorComponent";

import globalStyles from "./csGlobalStyles.module.scss"; // Entry point for CS global styles

import Theater from "../Theater/Theater";
import Ide from "../Ide/Ide";
import styles from "./CsController.module.scss";
import {Panel, PanelGroup, PanelResizeHandle} from "react-resizable-panels";
import {useCsContext} from "../../state/CsInstance";
import {TheaterSize} from "../../state/theaterStore";
import Sidebar from "../Sidebar/Sidebar";
import DragBar from "../../components/DragBar/DragBar";
import GlobalModal from "../Modals/GlobalModal";

export type Props = {
	width?: CSSProperties["width"];
	height?: CSSProperties["height"];
};

/**
 * The unit for the Panel component size props is percentage of the parent container.
 * The defaultSize is the Medium theater size from the original designs, and minSize is based on the Small theater size.
 *
 * To calculate the number needed for Panel props, we'll use the following formula:
 * originalSize px / 1280 px (default assumed screen size) * 100
 */
const DEFAULT_THEATER_WIDTH_IN_PERCENT = 42.5; // originalSize = 544px
const MIN_THEATER_COLUMN_WIDTH_IN_REM = 13.25; // The minimum theater column width that prevents the theater controls from overlapping.
const PX_PER_REM = 16;

// Main Component
const CsController = (props: Props) => {
	const {width, height} = props;
	const {theaterStore, userInputStore, uiStore, featureFlagStore} =
		useCsContext();
	const [viewportWidth, setViewportWidth] = useState(
		document.documentElement.clientWidth
	);
	const [, rerender] = useState(0);

	React.useEffect(() => {
		// Get flags from server-side and store
		(async () => {
			await featureFlagStore.evaluateFlags();
		})();
		// ONLY grab feature flags ONCE
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Listen to theater size changes
	useEffect(rerenderListener, [theaterStore.theaterSize]);
	function rerenderListener() {
		rerender((prev) => prev + 1);
	}

	let minTheaterWidthPercentage = calculateMinTheaterWidthPercentage();

	function onPanelLayoutChange() {
		const theaterPanel = document.getElementById("aopsTheaterCanvas");
		const containerDivWidth = theaterPanel?.clientWidth || 0;
		userInputStore.updateTheaterScaleFromContainerDivWidth(containerDivWidth);
	}

	function calculateMinTheaterWidthPercentage() {
		const theaterWidth = MIN_THEATER_COLUMN_WIDTH_IN_REM * PX_PER_REM;
		return (theaterWidth / viewportWidth) * 100;
	}

	// Recalculate theater size boundaries when window changes
	const onResize = () => {
		setViewportWidth(window.innerWidth);
		minTheaterWidthPercentage = calculateMinTheaterWidthPercentage();
	};
	window.onresize = onResize;

	// If theater size is None or theaterSizeFixed is true, render without the new draggable bar
	const panelsSection =
		theaterStore.theaterSize === TheaterSize.None ||
		featureFlagStore.theaterSizeFixed ? (
			<>
				<Theater />
				<DragBar />
				<Ide />
			</>
		) : (
			<PanelGroup
				autoSaveId={"csController"}
				direction={"horizontal"}
				onLayout={(layout) => {
					// TODO convert to useEffect or something less hacky
					// Hack to wait for the theater to rerender before updating the scale
					setTimeout(onPanelLayoutChange, 0);
				}}
			>
				<Panel
					defaultSize={DEFAULT_THEATER_WIDTH_IN_PERCENT}
					maxSize={99} // Max 100% gets too hard to grab the handle
					minSize={minTheaterWidthPercentage}
					style={{display: "flex", flexDirection: "row"}}
				>
					<Theater />
				</Panel>
				<PanelResizeHandle className={styles.resizeHandle} />
				<Panel>
					<Ide />
				</Panel>
			</PanelGroup>
		);

	return (
		<div
			className={[
				styles.csControllerWrapper,
				globalStyles.csControllerWrapper,
			].join(" ")}
			style={{width, height}}
		>
			<ErrorBoundary fallback={<ErrorComponent />}>
				<GlobalModal />
				{uiStore.isSidebarShown && <Sidebar />}
				{panelsSection}
			</ErrorBoundary>
		</div>
	);
};

export default observer(CsController);
